import React, { useState } from 'react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { AlertTriangle, Send, Shield, Loader2 } from 'lucide-react';
import apiClient from '../services/api';

const ReportPage = () => {
  const [formData, setFormData] = useState({ campaignUrl: '', reason: '', details: '', email: '' });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const reasons = [
    'Fraudulent or misleading',
    'Hate speech or discrimination',
    'Illegal activity',
    'Spam or scam',
    'Harassment',
    'Other concern',
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      await apiClient.post('/enquiries/report', {
        campaign_url: formData.campaignUrl,
        reason: formData.reason,
        details: formData.details,
        email: formData.email || null,
        name: formData.email ? formData.email.split('@')[0] : null
      });
      setSubmitted(true);
    } catch (err) {
      console.error('Failed to submit report:', err);
      setError('Failed to submit report. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="py-12">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="w-16 h-16 bg-gradient-to-br from-amber-500 to-red-500 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <AlertTriangle className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-4xl font-bold text-foreground mb-4">Report a Campaign</h1>
            <p className="text-xl text-muted-foreground">Help us keep FundTheHope safe and trustworthy</p>
          </div>

          {submitted ? (
            <div className="bg-card border border-border rounded-xl p-8 text-center">
              <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="w-8 h-8 text-green-500" />
              </div>
              <h2 className="text-xl font-semibold text-foreground mb-2">Report Submitted</h2>
              <p className="text-muted-foreground">Thank you for helping keep our community safe. Our Trust & Safety team will review your report within 24-48 hours.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="bg-card border border-border rounded-xl p-8 space-y-6">
              {error && (
                <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-500 text-sm">
                  {error}
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Campaign URL *</label>
                <Input value={formData.campaignUrl} onChange={(e) => setFormData({...formData, campaignUrl: e.target.value})} placeholder="https://fundthehope.com/campaign/..." required />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Reason for Report *</label>
                <select value={formData.reason} onChange={(e) => setFormData({...formData, reason: e.target.value})} className="w-full p-3 bg-background border border-input rounded-md" required>
                  <option value="">Select a reason</option>
                  {reasons.map((reason) => (<option key={reason} value={reason}>{reason}</option>))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Details *</label>
                <Textarea value={formData.details} onChange={(e) => setFormData({...formData, details: e.target.value})} placeholder="Please provide as much detail as possible..." rows={5} required />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Your Email (optional)</label>
                <Input type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} placeholder="For follow-up if needed" />
              </div>

              <Button type="submit" disabled={loading} className="w-full bg-gradient-to-r from-amber-500 to-red-500">
                {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting...</> : <><Send className="w-4 h-4 mr-2" /> Submit Report</>}
              </Button>

              <p className="text-xs text-muted-foreground text-center">All reports are kept confidential. False reports may result in account action.</p>
            </form>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default ReportPage;
